import { useId } from 'react';
import RadioCardGroup from '../ui/RadioCardGroup';
import SelectableCard from '../ui/SelectableCard';
import InfoSheet from '../ui/InfoSheet';
import Icon from '../ui/Icon';
import questionInfo from '../content/questionInfo';

/**
 * One question with its answer options.
 *
 * Replaces two near-verbatim ~60-line branches in DynamicForm (radio and checkbox). They
 * differed in exactly two ways: the input type, and how "is this selected?" is computed.
 * Everything else was duplicated, so a change to option rendering had to be made twice.
 *
 * THE ACCORDION IS GONE. Answers used to be hidden until the question was expanded; the
 * redesign leaves every answer visible and makes the row itself the control. The explanatory
 * prose that justified an accordion moved into InfoSheet, which is a better home for it because
 * it can be dismissed without also hiding the thing the customer came to choose.
 *
 * The question title appears twice in the DOM -- as the visible heading and as the fieldset
 * legend -- but is announced ONCE. The visible copy is aria-hidden and the legend names the radio
 * group. That costs the Details button nothing: aria-describedby resolves the text of its target
 * even when that target is aria-hidden, so a keyboard user who tabs straight to the trigger still
 * hears which question it belongs to. It is why `titleId` stays on the visible element rather
 * than moving to the legend.
 *
 * @param {object} props
 * @param {object} props.question         a question from product-config
 * @param {*} props.value                 answer state: a string (radio) or {id: bool} (checkbox)
 * @param {string} props.term             'monthly' | 'yearly'
 * @param {Function} props.getPrice       (answer) => number|null, resolved for the current plan
 * @param {Function} props.onAnswer       (questionId, answerId) => void
 */
export default function QuestionCard({ question, value, term, getPrice, onAnswer }) {
  const isRadio = question.type === 'radio';
  const titleId = useId();
  const info = questionInfo[question.id];

  // The answer this question currently resolves to, if the customer has picked a priced one.
  const priceFor = (answer) => {
    const price = getPrice(answer);
    return price ? `$${price.toFixed(2)}` : undefined;
  };

  return (
    <div
      className={`rounded-sm border ${
        // A selected question card takes the accent border, so a customer scanning section 3
        // can see what is left to answer without reading every row.
        value ? 'border-primary-600' : 'border-gray-200'
      } bg-white`}
    >
      {/*
        THE TITLE AND THE DISCLOSURE SHARE A LINE, which is why InfoSheet renders the row and the
        title goes in as its `summary`. The trigger has to sit on the question's own line while
        the panel expands below the whole row, and only the component owning the row can place
        both.

        16px question against 14px answers: they used to be the same size, so the question did not
        outrank the options it was asking about.
      */}
      <div className="border-b border-gray-100 p-3.5 sm:px-4">
        {info ? (
          <InfoSheet
            info={info}
            describedBy={titleId}
            summary={
              <>
                {question.icon && (
                  <Icon
                    name={question.icon}
                    strokeWidth={1.75}
                    className="h-[18px] w-[18px] flex-shrink-0 self-start text-primary-600 sm:mt-0 sm:h-5 sm:w-5"
                  />
                )}
                <div
                  id={titleId}
                  aria-hidden="true"
                  className="min-w-0 flex-1 text-base font-semibold leading-snug tracking-heading text-gray-900"
                >
                  {question.title}
                </div>
              </>
            }
            actions={info.actions.map((action) => {
              const answer = question.answers.find((a) => a.id === action.answerId);
              return { ...action, priceLabel: answer ? priceFor(answer) : undefined };
            })}
            onAction={(answerId) => onAnswer(question.id, answerId)}
          />
        ) : (
          /*
            No explanatory copy, so no disclosure and no row to share -- the title stands alone.
            questionInfo.test.jsx already fails the build if a configured question is missing its
            entry, so this branch is for safety rather than for any shipping question.
          */
          <div className="flex items-center gap-2.5">
            {question.icon && (
              <Icon
                name={question.icon}
                strokeWidth={1.75}
                className="h-[18px] w-[18px] flex-shrink-0 text-primary-600 sm:h-5 sm:w-5"
              />
            )}
            <div
              id={titleId}
              aria-hidden="true"
              className="min-w-0 flex-1 text-base font-semibold leading-snug tracking-heading text-gray-900"
            >
              {question.title}
            </div>
          </div>
        )}
      </div>

      <RadioCardGroup legend={question.title} className="flex flex-col">
        {question.answers.map((answer) => {
          // The ONLY real difference between the old radio and checkbox branches.
          const checked = isRadio ? value === answer.id : value?.[answer.id] === true;

          const price = getPrice(answer);
          const productType = answer.addProduct?.type || null;
          const isSubscription = productType === 'subscription';

          return (
            <SelectableCard
              key={answer.id}
              density="row"
              type={isRadio ? 'radio' : 'checkbox'}
              // id shape preserved: `question-<questionId>-<answerId>`. Shared URLs and tests
              // depend on these being stable.
              id={`question-${question.id}-${answer.id}`}
              name={isRadio ? `question-${question.id}` : `question-${question.id}-${answer.id}`}
              value={answer.id}
              checked={checked}
              onChange={() => onAnswer(question.id, answer.id)}
              title={answer.label}
              subtitle={answer.description}
              priceLabel={price ? `$${price.toFixed(2)}` : undefined}
              priceSuffix={
                price
                  ? isSubscription
                    ? term === 'monthly'
                      ? '/mo'
                      : '/yr'
                    : 'One-time'
                  : undefined
              }
            />
          );
        })}
      </RadioCardGroup>
    </div>
  );
}
